import { resolveFundComplianceGatewayBaseUrl, resolveFundComplianceGatewayWsUrl } from "@/lib/fund-compliance/gateway";
import type { JsonRecord } from "@/lib/fund-compliance/parse-primitives";
import { parseDateValue, readString, resolveString, toRecord } from "@/lib/fund-compliance/parse-primitives";

export class FundComplianceAuthError extends Error {
  constructor(message = "UNAUTHENTICATED") {
    super(message);
    this.name = "FundComplianceAuthError";
  }
}

export interface FundComplianceCurrentUser {
  authenticated: true;
  userId?: string;
  username?: string;
  displayName?: string;
  email?: string;
  roles?: string[];
}

export interface FundComplianceSkill {
  id: string;
  name?: string;
  description?: string;
}

export interface FundComplianceChatSummary {
  id: string;
  title: string;
  skillId?: string;
  createdAt?: number;
}

export interface FundComplianceFileRef {
  storageId: string;
  filename: string;
  mimeType: string;
  storageName: string;
  filesize?: string;
  isUploading?: boolean;
}

async function fundComplianceFetch(path: string, init?: RequestInit) {
  const res = await fetch(`${resolveFundComplianceGatewayBaseUrl()}${path}`, {
    credentials: "include",
    ...init,
  });

  if (res.status === 401) {
    throw new FundComplianceAuthError();
  }

  return res;
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Request failed: ${res.status}`);
  }

  return (await res.json()) as T;
}

export async function exchangeFundComplianceAccessCode(accessCode: string) {
  const res = await fundComplianceFetch("/api/oauth2/access-code", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ access_code: accessCode.trim() }),
  });

  return parseJson<{ status?: string; authenticated?: boolean; user?: JsonRecord }>(res);
}

export async function fetchFundComplianceCurrentUser() {
  const res = await fundComplianceFetch("/api/users/me", {
    method: "GET",
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch current user: ${res.status}`);
  }

  return (await res.json()) as FundComplianceCurrentUser;
}

export async function fetchFundComplianceSkills() {
  const res = await fundComplianceFetch("/api/keith/agent/skills");
  const payload = await parseJson<unknown>(res);
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(toRecord(payload)?.skills)
      ? (toRecord(payload)?.skills as unknown[])
      : [];

  const skills = list
    .map((item) => {
      const record = toRecord(item);
      if (!record) return null;
      const id = resolveString(record, ["id", "skillId", "slug"]);
      if (!id) return null;
      return {
        id,
        name: resolveString(record, ["name", "title"]),
        description: readString(record.description),
      } satisfies FundComplianceSkill;
    })
    .filter(Boolean) as FundComplianceSkill[];

  return skills.length > 0 ? skills : [{ id: "compliance-qa", name: "Compliance QA" }];
}

export async function createFundComplianceChat(skillId: string, title: string) {
  const res = await fundComplianceFetch(`/api/keith/agent/${encodeURIComponent(skillId)}/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ title }),
  });
  const payload = await parseJson<unknown>(res);
  const record = toRecord(payload);
  const nestedChat = toRecord(record?.chat);
  const chatId = readString(record?.chatId) ?? readString(nestedChat?.id) ?? readString(record?.id);

  if (!chatId) {
    throw new Error("Chat creation response did not include chatId");
  }

  return {
    chatId,
    skillId: readString(record?.skillId) ?? skillId,
    title,
  };
}

export async function fetchFundComplianceChats() {
  const res = await fundComplianceFetch("/api/keith/agent/chats");
  const payload = await parseJson<unknown>(res);
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(toRecord(payload)?.chats)
      ? (toRecord(payload)?.chats as unknown[])
      : [];

  return list
    .map((item) => {
      const record = toRecord(item);
      if (!record) return null;
      const id = resolveString(record, ["id", "chatId"]);
      if (!id) return null;
      return {
        id,
        title: readString(record.title) ?? "Untitled chat",
        skillId: readString(record.skillId),
        createdAt:
          parseDateValue(record.createdAt) ??
          parseDateValue(record.updatedAt) ??
          parseDateValue(record.timestamp),
      } satisfies FundComplianceChatSummary;
    })
    .filter(Boolean) as FundComplianceChatSummary[];
}

export async function deleteFundComplianceChat(chatId: string) {
  const res = await fundComplianceFetch(`/api/keith/agent/chats/${encodeURIComponent(chatId)}`, {
    method: "DELETE",
  });

  if (res.status === 204) {
    return;
  }

  await parseJson<unknown>(res);
}

export interface FundComplianceMessagesPage {
  messages: unknown[];
  nextBefore: string | null;
  hasMore: boolean;
}

export async function fetchFundComplianceChatMessages(
  chatId: string,
  options?: { before?: string; limit?: number }
): Promise<FundComplianceMessagesPage> {
  const params = new URLSearchParams();
  if (options?.before) params.set("before", options.before);
  if (options?.limit !== undefined) params.set("limit", String(options.limit));
  const query = params.toString();
  const url = `/api/keith/agent/chats/${encodeURIComponent(chatId)}/messages${query ? `?${query}` : ""}`;

  const res = await fundComplianceFetch(url);
  const payload = await parseJson<unknown>(res);

  if (Array.isArray(payload)) {
    return { messages: payload, nextBefore: null, hasMore: false };
  }

  const record = toRecord(payload);
  const messages = Array.isArray(record?.messages) ? (record?.messages as unknown[]) : [];
  const nextBefore = readString(record?.nextBefore) ?? null;
  const hasMore = typeof record?.hasMore === "boolean" ? record.hasMore : nextBefore !== null;

  return { messages, nextBefore, hasMore };
}

export async function uploadFundComplianceFile(file: File): Promise<FundComplianceFileRef> {
  const formData = new FormData();
  formData.append("file", file, file.name);

  const res = await fundComplianceFetch("/api/storage/v1/files", {
    method: "POST",
    body: formData,
  });

  const payload = await parseJson<unknown>(res);
  const record = toRecord(payload);
  const storageId = readString(record?.id);
  const storageName = readString(record?.storageName);

  if (!storageId || !storageName) {
    throw new Error("Upload response did not include storage file identifiers");
  }

  return {
    storageId,
    storageName,
    filename: file.name,
    mimeType: file.type || "application/octet-stream",
    filesize: formatFileSize(file.size),
  };
}

export function formatFileSize(sizeInBytes: number) {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) return "0 B";
  if (sizeInBytes < 1024) return `${sizeInBytes} B`;
  if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getFundComplianceSocketUrl() {
  return `${resolveFundComplianceGatewayWsUrl()}/keith/ws`;
}
